// Spec 044 v2: admin variant-level review for a single offering.
// Groups pending variants by supported car; per-row Approve/Reject/Request changes.

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { PendingVariantsResponse } from '../types';

type ActionMode = 'reject' | 'request-changes';

interface ReasonModalState {
  variantId: string;
  mode: ActionMode;
  summary: string;
}

export default function OfferingPendingVariantsPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { offeringId } = useParams<{ offeringId: string }>();
  const [data, setData] = useState<PendingVariantsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [reason, setReason] = useState<ReasonModalState | null>(null);
  const [reasonText, setReasonText] = useState('');

  const refresh = useCallback(() => {
    if (!auth.token || !offeringId) return;
    setLoading(true);
    setError(null);
    api.listPendingVariants(offeringId, auth.token)
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [auth.token, offeringId]);

  useEffect(() => { refresh(); }, [refresh]);

  const approveOne = async (variantId: string) => {
    if (!auth.token || !offeringId) return;
    setBusyId(variantId);
    try {
      await api.approveVariant(offeringId, variantId, auth.token);
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  };

  const approveCarGroup = async (variantIds: string[]) => {
    if (!auth.token || !offeringId || variantIds.length === 0) return;
    try {
      await api.approveVariantsBulk(offeringId, variantIds, auth.token);
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const openReason = (variantId: string, mode: ActionMode, summary: string) => {
    setReason({ variantId, mode, summary });
    setReasonText('');
  };

  const submitReason = async () => {
    if (!auth.token || !offeringId || !reason) return;
    if (!reasonText.trim()) { setError('Reason is required.'); return; }
    try {
      if (reason.mode === 'reject') {
        await api.rejectVariant(offeringId, reason.variantId, reasonText.trim(), auth.token);
      } else {
        await api.requestVariantChanges(offeringId, reason.variantId, reasonText.trim(), auth.token);
      }
      setReason(null);
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <p>Loading pending variants…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="admin-page">
        <p style={{ color: '#dc2626' }}>{error}</p>
        <button className="admin-btn admin-btn-secondary" onClick={refresh}>Retry</button>
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="admin-page admin-pending-variants">
      <div className="admin-pending-variants__breadcrumb">
        <button onClick={() => navigate(-1)} className="admin-btn admin-btn-text">‹ Back</button>
        <span>Service approvals</span>
        <span>/</span>
        <strong>{data.offeringName}</strong>
      </div>

      <div className="admin-pending-variants__header">
        <h2>{data.garageName}</h2>
        <p className="admin-page-subtitle">
          {data.totalPending} variant{data.totalPending === 1 ? '' : 's'} pending review · {data.carGroups.length} car{data.carGroups.length === 1 ? '' : 's'}
        </p>
      </div>

      {data.totalPending === 0 ? (
        <div className="admin-empty">
          <p>No pending variants for review.</p>
        </div>
      ) : (
        <div className="admin-pending-variants__list">
          {data.carGroups.map((group) => (
            <section key={group.supportedCarId || 'unscoped'} className="admin-pending-variants__car-group">
              <header className="admin-pending-variants__car-header">
                <div>
                  <strong>{group.carMakeName || ''} {group.carModelName || ''}</strong>
                  <span className="admin-pending-variants__car-meta">
                    {group.variants.length} pending
                  </span>
                </div>
                <button className="admin-btn admin-btn-primary admin-btn-sm"
                  onClick={() => approveCarGroup(group.variants.map((v) => v.variantId))}
                  disabled={group.variants.length === 0}>
                  ✓ Approve all ({group.variants.length})
                </button>
              </header>

              <div className="admin-pending-variants__rows">
                {group.variants.map((variant) => {
                  const isBusy = busyId === variant.variantId;
                  const comboLabel = variant.variationValues.length === 0
                    ? 'Single price'
                    : variant.variationValues.map((vv) => `${vv.variationName || ''}: ${vv.label}`).join(' · ');
                  const summary = `${group.carMakeName || ''} ${group.carModelName || ''} · ${comboLabel} · ${variant.price} AED`;
                  return (
                    <div key={variant.variantId} className="admin-pending-variants__variant-row">
                      <div className="admin-pending-variants__variant-combo">
                        {variant.variationValues.length === 0 ? (
                          <em>Single price</em>
                        ) : (
                          variant.variationValues.map((vv) => (
                            <span key={vv.variationValueId} className="admin-variation-chip">
                              {vv.variationName ? `${vv.variationName}: ` : ''}{vv.label}
                            </span>
                          ))
                        )}
                      </div>
                      <div className="admin-pending-variants__variant-price">
                        {variant.price.toLocaleString()} AED
                        {variant.durationOverrideMinutes != null && (
                          <span className="admin-pending-variants__variant-duration"> · {variant.durationOverrideMinutes} min</span>
                        )}
                      </div>
                      <div className="admin-pending-variants__variant-status">
                        <span className="admin-badge pending">PENDING</span>
                      </div>
                      <div className="admin-pending-variants__variant-actions">
                        <button className="admin-icon-btn admin-icon-btn--approve" title="Approve"
                          onClick={() => approveOne(variant.variantId)} disabled={isBusy}>✓</button>
                        <button className="admin-icon-btn admin-icon-btn--reject" title="Reject"
                          onClick={() => openReason(variant.variantId, 'reject', summary)} disabled={isBusy}>✕</button>
                        <button className="admin-icon-btn admin-icon-btn--request" title="Request changes"
                          onClick={() => openReason(variant.variantId, 'request-changes', summary)} disabled={isBusy}>↻</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}

      {reason && (
        <div className="admin-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setReason(null); }}>
          <div className="admin-modal admin-reason-modal">
            <header className="admin-modal-header">
              <h3>{reason.mode === 'reject' ? 'Reject variant' : 'Request changes'}</h3>
              <button onClick={() => setReason(null)} className="admin-modal-close">&times;</button>
            </header>
            <div className="admin-modal-body">
              <p className="admin-modal-summary">{reason.summary}</p>
              <label>
                Reason *
                <textarea rows={4} value={reasonText}
                  onChange={(e) => setReasonText(e.target.value)}
                  placeholder="Visible to the partner." />
              </label>
            </div>
            <footer className="admin-modal-footer">
              <button className="admin-btn admin-btn-secondary" onClick={() => setReason(null)}>Cancel</button>
              <button className="admin-btn admin-btn-primary" onClick={submitReason} disabled={!reasonText.trim()}>
                {reason.mode === 'reject' ? 'Reject' : 'Request changes'}
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
